import { Component } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RotateCw, Home } from 'lucide-react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console — production-safe; nothing leaves the browser
    console.error('App-level error caught by boundary:', error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="p-6 sm:p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 mb-4">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-sm text-gray-600 mb-6">
              We hit an unexpected error. Your data is safe — try reloading the page.
            </p>
            {this.state.error?.message && (
              <details className="text-left mb-6 group">
                <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                  Technical details
                </summary>
                <pre className="mt-2 text-[10px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={this.handleReload}
                className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                data-testid="error-reload-btn"
              >
                <RotateCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
              <Button
                onClick={this.handleHome}
                variant="outline"
                className="flex-1"
                data-testid="error-home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </div>
          </div>
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              If this keeps happening, reach out — we'll fix it fast.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
